// Example hooks using TanStack Query with JWT authentication
// These demonstrate how to use the API client with React Query

import { api, createMutation, fetchQuery, login, logout } from "../lib/api";
import {
  AuthLoginResult,
  AuthRefreshTokenData,
  AuthRefreshTokenErrorResult,
  AuthRefreshTokenResult,
  BloodTypeResult,
  CountryResult,
  EventDetailsResult,
  EventHistoryResult,
  EventRegistrationRequest,
  EventRegistrationResult,
  ResendVerifyEmailData,
  ResendVerifyEmailResult,
  UpdateProfileData,
  UpdateProfileErrorResult,
  UpdateProfileResult,
  UserProfileResult,
  UserSignupData,
  UserSignupErrorResult,
  UserSignupResult,
  VerifyEmailResult
} from "@/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery } from "@tanstack/react-query";

// ============================================================================
// Query Hooks (GET requests)
// ============================================================================

/**
 * Fetch user profile
 */
export function useUserProfile() {
  return useQuery({
    queryKey: ["/api/v1/ultimate/profile"],
    queryFn: fetchQuery as () => Promise<UserProfileResult>,
  });
}

/**
 * Fetch country list
 */
export function useCountryList() {
  return useQuery({
    queryKey: ["/api/v1/resources/master_country?pageSize=300"],
    queryFn: fetchQuery as () => Promise<CountryResult>,
  });
}

/**
 * Fetch blood type list
 */
export function useBloodTypeList() {
  return useQuery({
    queryKey: ["/api/v1/resources/master_blood_type?pageSize=30"],
    queryFn: fetchQuery as () => Promise<BloodTypeResult>,
  });
}

/**
 * Verify email
 */
export function useVerifyEmail(token: string) {
  return useQuery({
    queryKey: ["/api/v1/ultimate/verify?token=" + token],
    queryFn: fetchQuery as () => Promise<VerifyEmailResult>,
    enabled: !!token,
  })
}

/**
 * Fetch Event Details
 */
export function useFetchEventDetails(eventId: string) {
  return useQuery({
    queryKey: ["/api/v1/ultimate_event/" + eventId],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: fetchQuery as () => Promise<EventDetailsResult>,
  })
}

/**
 * Fetch Event History
 */
export function useFetchEventHistory() {
  return useQuery({
    queryKey: [`/api/v1/ultimate/event-history/${process.env.REACT_APP_EVENT_ID}`],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: fetchQuery as () => Promise<EventHistoryResult>,
  })
}

/**
 * Fetch list with pagination and filters
 */
export function useUsersList(params?: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  return useQuery({
    queryKey: ["/users", params],
    queryFn: fetchQuery,
    // Keep previous data while fetching new data (for pagination)
    placeholderData: (previousData) => previousData,
  });
}

// ============================================================================
// Mutation Hooks (POST, PUT, PATCH, DELETE)
// ============================================================================

/**
 * User refresh token hooks
 */
export function useAuthRefreshToken() {
  return useMutation({
    mutationFn: createMutation<{
      data: AuthRefreshTokenData
    }, AuthRefreshTokenResult>("/api/v1/ultimate/refresh", "POST"),
    onSuccess: (data: AuthRefreshTokenResult) => {
      console.log('AuthRefreshTokenData', data);
    },
    onError: (error: AuthRefreshTokenErrorResult) => {
      console.log('AuthLoginErrorResult', error);
    }
  })
}

/**
 * User signup hooks
 */
export function useSignupUser() {
  return useMutation({
    mutationFn: createMutation<{
      data: UserSignupData
    }, UserSignupResult>("/api/v1/ultimate/signup", "POST"),
    onSuccess: (data: UserSignupResult) => {
      console.log('UserSignupData', data);
    },
    onError: (error: UserSignupErrorResult) => {
      console.log('UserSignupData', error);
    }
  })
}

/**
 * Resend verify email hooks
 */
export function useResendVerifyEmail() {
  return useMutation({
    mutationFn: createMutation<{
      data: ResendVerifyEmailData
    }, ResendVerifyEmailResult>("/api/v1/ultimate/resend/verify", "POST"),
    onSuccess: (data: ResendVerifyEmailResult) => {
      console.log(data);
    }
  })
}

/**
 * Registration event
 * Accepts { data: [payload] } format as per API.md
 */
export function useRegistrationEvent(eventId: string) {
  return useMutation({
    mutationFn: createMutation<EventRegistrationRequest, EventRegistrationResult>(
      "/api/v1/ultimate_registration/" + eventId,
      "POST"
    ),
    onSuccess: (data: EventRegistrationResult) => {
      console.log('EventRegistrationResult', data);
    },
    onError: (error: EventRegistrationResult) => {
      console.log('EventRegistrationError', error);
    }
  })
}

/**
 * Update profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMutation<UpdateProfileData, UpdateProfileResult>(
      "/api/v1/ultimate/profile",
      "PATCH"
    ),
    onSuccess: (data: UpdateProfileResult) => {
      console.log('UpdateProfileResult', data);
    },
    onError: (error: UpdateProfileErrorResult) => {
      console.log('UpdateProfileError', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/v1/ultimate/profile"] });
    }
  })
}

// ============================================================================
// Authentication Hooks
// ============================================================================

/**
 * Login hook
 */
export function useLogin() {
  // const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    onSuccess: (data: AuthLoginResult) => {
      console.log('AuthLoginResult', data);
      // Optionally prefetch user data after login
      // queryClient.prefetchQuery({
      //   queryKey: ["/users", data.user.id],
      //   queryFn: fetchQuery,
      // });
    },
  });
}

/**
 * Logout hook
 */
export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      logout();
      return Promise.resolve();
    },
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.clear();
    },
  });
}

// ============================================================================
// Optimistic Updates Example
// ============================================================================
/**
 * Example: Toggle like with optimistic update
 */
export function useToggleLike() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: string) =>
      createMutation<{ liked: boolean }>(`/posts/${postId}/like`, "POST")({
        liked: true,
      }),
    // Optimistically update the cache
    onMutate: async (postId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/posts", postId] });

      // Snapshot the previous value
      const previousPost = queryClient.getQueryData(["/posts", postId]);

      // Optimistically update to the new value
      queryClient.setQueryData(["/posts", postId], (old: unknown) => ({
        ...(old as Record<string, unknown>),
        liked: !(old as Record<string, unknown>)?.liked,
        likesCount:
          ((old as Record<string, unknown>)?.likesCount as number) +
          (((old as Record<string, unknown>)?.liked as boolean) ? -1 : 1),
      }));

      // Return a context object with the snapshotted value
      return { previousPost };
    },
    // If the mutation fails, use the context to roll back
    onError: (err, postId, context) => {
      if (context?.previousPost) {
        queryClient.setQueryData(["/posts", postId], context.previousPost);
      }
    },
    // Always refetch after error or success
    onSettled: (data, error, postId) => {
      queryClient.invalidateQueries({ queryKey: ["/posts", postId] });
    },
  });
}

/**
 * Example: Infinite scroll list
 */
export function useInfiniteUsers() {
  return useInfiniteQuery({
    queryKey: ["/users/infinite"],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get(`/users?page=${pageParam}&limit=10`);
      return response as {
        users: unknown[];
        nextPage: number | null;
        hasMore: boolean;
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextPage : undefined;
    },
    initialPageParam: 1,
  });
}
